'use strict';
import util from './util';



/**
 * A module pattern that defines a `createSlidePanel` function for managing a slide panel UI component.
 * Supports AMD, CommonJS, and browser global environments.
 *
 * @param {string} panelSelector - The CSS selector for the panel element.
 * @returns {Object} An object with methods to show, close, and fetch data for the panel.
 *
 * Methods:
 * - show(data, callback): Displays the panel with the provided data and executes a callback if provided.
 * - close(): Closes the panel and clears its content.
 * - fetchDataAndShow(url, callback): Fetches data from a URL and displays it in the panel, executing a callback if provided.
 *
 * The panel requires specific elements: a panel body, a close icon, and an overlay.
 * Logs an error if any required elements are missing.
 */

function createSlidePanel(panelSelector, options = {}) {
        const panel = document.querySelector(panelSelector);
        const overlay = document.querySelector('.panel-overlay');
        const panelBody = panel ? panel.querySelector('.panel-body') : null;
        const closeIcon = panel ? panel.querySelector('.icon-close') : null;
        let currentClassName = '';

        if (!panel || !overlay || !panelBody || !closeIcon) {
            console.error('Error: One or more required elements are missing.');
            return null;
        }

        // Apply custom class and ID if provided
        if (options.className) {
            panel.classList.add(options.className);
        }
        if (options.id) {
            panel.id = options.id;
        }

        function show(data, callback, className) {
            currentClassName = className;
            panel.classList.add(className);
            updatePanelBody(data);
            panel.classList.add('opened');
            $('html').addClass('modal-open');
            if (callback && typeof callback === 'function') {
                callback();
            }
        }

        function close() {
            panel.classList.remove(currentClassName);
            panel.classList.remove('opened');
            $('html').removeClass('modal-open');
            $('body').removeClass('body-fixed');
            clearPanelBody();
        }

        function updatePanelBody(data) {
            // Check if session has timed out
            util.checkSessionTimeout(data);
            // Assuming data is HTML content or a string
            panelBody.innerHTML = data;
        }

        function clearPanelBody() {
            panelBody.innerHTML = '';
        }

        function fetchDataAndShow(url, callback) {
            fetch(url)
                .then(response => response.text())
                .then(data => show(data, callback))
                .catch(error => console.error('Error fetching data:', error));
        }

        closeIcon.addEventListener('click', close);
        overlay.addEventListener('click', close);

        return {
            show,
            close,
            fetchDataAndShow
        };
    }

export default createSlidePanel;

/** Usage -
 * To add class and ID - const slidePanel = createSlidePanel('#myPanel', { className: 'custom-class', id: 'custom-id' });
 * To show the panel with dynamic data - slidePanel.fetchDataAndShow('/api/get-panel-data', () => {console.log('Panel is now open with new data.');});
 * To close the panel - slidePanel.close();
 * showSlidePanel with a callback - slidePanel.show(response, () => { console.log('Panel is now open');// callback/Additional actions can be performed here});
 */